import { DateStep, humanReadableDateStep } from '@frontend/common';
import { ICONS } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import React from 'react';
import { createPortal } from 'react-dom';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';

import { useFromToDateInput } from './from-to-date-input.controller';

const ALL_PRESET_STEPS = (Object.keys(DateStep) as (keyof typeof DateStep)[]).filter((key) => key !== 'CUSTOM').map((key) => DateStep[key]);

export interface FromToDateInputProps {
    onChange: (from: string | null, to: string | null) => void;
    onClick: (value: string) => void;
    period?: DateStep | null;
    onNavigateBack?: () => void;
    onNavigateForward?: () => void;
    fromDate?: string | null;
    toDate?: string | null;
    /** When set, only these presets are shown (CUSTOM is never listed). Defaults to all presets. */
    allowedDateSteps?: DateStep[];
    /** When set, custom From/To spans longer than this are clamped; native inputs get min/max bounds. */
    maxRangeMs?: number;
    /** Optional label overrides for preset rows (e.g. Full period with max window). */
    dateStepLabelOverrides?: Partial<Record<DateStep, React.ReactNode>>;
}

export const FromToDateInput = (props: FromToDateInputProps) => {
    const viewProps = useFromToDateInput(props);
    const presetSteps = props.allowedDateSteps ?? ALL_PRESET_STEPS;

    const stepLabel = (step: DateStep): React.ReactNode => props.dateStepLabelOverrides?.[step] ?? humanReadableDateStep(step);

    const menuComponent = (
        <div
            ref={viewProps.menuRef}
            className='ignore-modal-close bg-background-surface border-border-default fixed z-50 flex flex-col items-start justify-start gap-4 self-stretch rounded-xl border'
            style={{
                top: `${viewProps.menuPosition.top}px`,
                left: `${viewProps.menuPosition.left}px`,
                width: `${viewProps.menuPosition.width}px`
            }}>
            <div className='w-full'>
                <div className='border-border-default inline-flex w-full flex-col items-start justify-start self-stretch border-b px-2 pt-2 pb-3'>
                    {presetSteps.map((step) => (
                        <div
                            key={step}
                            onClick={(e) => {
                                e.stopPropagation();
                                viewProps.setFromDate(null);
                                viewProps.setToDate(null);
                                viewProps.setShowMenu(false);
                                props.onClick(step);
                            }}
                            className='hover:bg-background-default inline-flex cursor-pointer items-center justify-start gap-2.5 self-stretch rounded-lg p-2'>
                            <div className='text-foreground-default justify-start text-base font-medium'>{stepLabel(step)}</div>
                        </div>
                    ))}
                </div>
                <div className='inline-flex w-full flex-col self-stretch px-4 pt-3 pb-4'>
                    <div className='flex flex-col items-start justify-start gap-2 self-stretch'>
                        <div className='text-foreground-default justify-start text-base font-medium'> {CommonMessage.FORMS.FROM_TIMESTAMP}</div>
                        <div className='bg-background-surface border-border-default inline-flex h-11 items-center justify-start gap-2 self-stretch overflow-hidden rounded-xl border px-4'>
                            <input
                                placeholder={new Date().toLocaleDateString()}
                                onClick={(e) => e.currentTarget.showPicker()}
                                type='date'
                                className='text-foreground-default border-none bg-transparent outline-none'
                                onChange={(e) => viewProps.handleDateChange(e, 'from')}
                                value={viewProps.fromDate ? viewProps.fromDate.split('T')[0] : ''}
                                min={viewProps.dateInputBounds.fromMin}
                                max={viewProps.dateInputBounds.fromMax}
                            />
                            <div className='flex w-full items-center justify-end gap-2'>
                                {viewProps.fromDate && (
                                    <ICONS.BUTTON.CLOSE
                                        className='text-foreground-default cursor-pointer'
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            viewProps.setFromDate(null);
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    <div className='flex flex-col items-start justify-start gap-2 self-stretch'>
                        <div className='text-foreground-default justify-start text-base font-medium'>{CommonMessage.FORMS.TO_TIMESTAMP}</div>
                        <div className='bg-background-surface border-border-default inline-flex h-11 w-full items-center justify-start gap-2 self-stretch overflow-hidden rounded-xl border px-4'>
                            <input
                                placeholder={new Date().toLocaleDateString()}
                                onClick={(e) => e.currentTarget.showPicker()}
                                type='date'
                                className='text-foreground-default border-none bg-transparent outline-none'
                                onChange={(e) => viewProps.handleDateChange(e, 'to')}
                                value={viewProps.toDate ? viewProps.toDate.split('T')[0] : ''}
                                min={viewProps.dateInputBounds.toMin}
                                max={viewProps.dateInputBounds.toMax}
                            />
                            <div className='flex w-full items-center justify-end gap-2'>
                                {viewProps.toDate && (
                                    <ICONS.BUTTON.CLOSE
                                        className='text-foreground-default cursor-pointer'
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            viewProps.setToDate(null);
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className='relative inline-flex h-full min-w-64 flex-col items-start justify-start gap-1'>
            <div className='flex h-full w-full items-center gap-2'>
                {props.onNavigateBack && (
                    <button
                        type='button'
                        onClick={(e) => {
                            e.stopPropagation();
                            props.onNavigateBack?.();
                        }}
                        className='bg-background-surface border-border-default hover:bg-background-default flex h-full cursor-pointer items-center justify-center rounded-xl border px-2'>
                        <LuChevronLeft className='text-foreground-default size-5' />
                    </button>
                )}

                <div
                    id='from-to-date-input'
                    onClick={() => viewProps.setShowMenu((prev) => !prev)}
                    className='bg-background-surface border-border-default inline-flex h-full flex-1 cursor-pointer items-center justify-between gap-2 self-stretch overflow-hidden rounded-xl border px-4'>
                    <div className='min-w-0 flex-1'>
                        {!props.fromDate && !props.toDate && !viewProps.fromDate && !viewProps.toDate && !props.period && (
                            <span className='text-foreground-default'>{CommonMessage.FORMS.PLACEHOLDER}</span>
                        )}
                        {(props.fromDate || props.toDate) && (
                            <div className='text-foreground-default flex items-center gap-2 truncate'>
                                {props.fromDate && <span>{new Date(props.fromDate).toLocaleDateString()}</span>}
                                {props.fromDate && props.toDate && <span>-</span>}
                                {props.toDate && <span>{new Date(props.toDate).toLocaleDateString()}</span>}
                            </div>
                        )}
                        {!props.fromDate && !props.toDate && (viewProps.fromDate || viewProps.toDate) && (
                            <div className='text-foreground-default flex items-center gap-2 truncate'>
                                {viewProps.fromDate && <span>{new Date(viewProps.fromDate).toLocaleDateString()}</span>}
                                {viewProps.fromDate && viewProps.toDate && <span>-</span>}
                                {viewProps.toDate && <span>{new Date(viewProps.toDate).toLocaleDateString()}</span>}
                            </div>
                        )}
                        {!props.fromDate && !props.toDate && !viewProps.fromDate && !viewProps.toDate && props.period && props.period !== DateStep.CUSTOM && (
                            <span className='text-foreground-default'>{stepLabel(props.period)}</span>
                        )}
                    </div>
                    <div className='flex shrink-0 items-center justify-start gap-2'>
                        {(viewProps.fromDate || viewProps.toDate) && (
                            <ICONS.BUTTON.CLOSE
                                className='text-foreground-default cursor-pointer'
                                onClick={(e) => {
                                    e.stopPropagation();
                                    viewProps.setFromDate(null);
                                    viewProps.setToDate(null);
                                }}
                            />
                        )}
                        <ICONS.BUTTON.CALENDAR className='text-foreground-default cursor-pointer' />
                    </div>
                </div>

                {props.onNavigateForward && (
                    <button
                        type='button'
                        onClick={(e) => {
                            e.stopPropagation();
                            props.onNavigateForward?.();
                        }}
                        className='bg-background-surface border-border-default hover:bg-background-default flex h-full cursor-pointer items-center justify-center rounded-xl border px-2'>
                        <LuChevronRight className='text-foreground-default size-5' />
                    </button>
                )}
            </div>
            {viewProps.showMenu && viewProps.portalRoot && createPortal(menuComponent, viewProps.portalRoot)}
        </div>
    );
};
